// types.ts — Datentypen für die INLINE-Verknüpfungserfassung der Kontaktanlage.
//
// Anders als die alte Tabellen+Popup-Lösung (RelationCollectSection +
// RelationCreateDialog) wird hier NICHTS während des Ausfüllens ins Backend
// geschrieben. Alle Einträge leben im Form-State (als JSON pro Kategorie) und
// werden ERST beim Klick auf „Speichern" persistiert (siehe persistRelations.ts).

import type { VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import type { WeitereKommunikationEintrag } from "../forms/widgets/WeitereKommunikationDialog";

export type RelKind =
  | "gfg"
  | "verant"
  | "firmaConn"
  | "personFirma"
  | "household"
  | "assistenz"
  /**
   * Das Gegenüber einer Statusrolle („Verwalter — für wen?").
   *
   * Eigene Art, weil die Rolle selbst entscheidet, WIE gespeichert wird: Die
   * Beteiligungs-Rollen werden zu Firma↔Firma-Verbindungen, alle anderen zu
   * einer Funktion am bestehenden Typ (`data/statusBezugMapping.ts`). In eine
   * der vorhandenen Arten gepresst wäre die Unterscheidung nicht ausdrückbar.
   */
  | "statusBezug"
  /**
   * Eine der fünf Vertretungsrollen, die dieser Kontakt FÜR einen anderen
   * ausübt (`data/vertretungsrollen.ts`). Richtung wie bei `personFirma`:
   * `relationType` sagt, was `contactDest` für `contactSrc` ist — der
   * Vertretene ist `src`, der Vertreter `dest`.
   */
  | "vertretung";

/**
 * Eine weitere Branchengruppe einer eingetippten Firma: die Katalog-id plus der
 * „Sonstiges"-Freitext, falls die Gruppe einen erlaubt.
 */
export type WeitereBranchengruppe = {
  branchengruppeId: number;
  freitext?: string | null;
};

/**
 * Liest `ContactRef.weitereBranchengruppen` in EINER Form — gleich, ob dort die
 * neue Objektform oder eine alte reine Zahl steht (zwischengespeicherter
 * Anlage-Entwurf aus der Zeit vor dem 09.09.2026).
 *
 * Ungültige Einträge fallen weg: Eine Zuordnung ohne positive id lässt sich
 * nicht speichern, und die drei anderen Stellen der Branchenlogik verwerfen sie
 * ebenfalls.
 */
export function weitereBranchenNormalisieren(
  liste: (number | WeitereBranchengruppe)[] | undefined | null,
): WeitereBranchengruppe[] {
  const out: WeitereBranchengruppe[] = [];
  for (const eintrag of liste ?? []) {
    const roh = typeof eintrag === "number" ? { branchengruppeId: eintrag } : eintrag;
    const id = Number(roh?.branchengruppeId);
    if (!Number.isFinite(id) || id <= 0) continue;
    out.push({
      branchengruppeId: id,
      freitext: typeof eintrag === "number" ? null : (eintrag.freitext ?? null),
    });
  }
  return out;
}

/**
 * Referenz auf einen Gegen-Kontakt einer Beziehung. ENTWEDER ein bestehender
 * Kontakt (`contactId` gesetzt, per Suche gewählt) ODER ein frei eingetippter
 * (`manual: true`) — dieser wird erst BEIM SPEICHERN via POST /contact angelegt.
 */
export type ContactRef = {
  contactId?: number;            // gesetzt = bestehender Kontakt (Suche)
  manual?: boolean;              // true = frei eingetippt (Anlage erst beim Speichern)
  type: "Person" | "Firma";
  firstName?: string;            // Person
  lastName?: string;             // Person: Nachname / Firma: Firmenname
  gender?: string;               // Person: Männlich/Weiblich/Divers/Ohne Angabe
  salutationId?: number;         // Person: aus GET /contactsalutation
  salutationLabel?: string;      // Anzeige + Haushalts-Rollen-Ableitung (Frau/Herr)
  titleId?: number;              // Person: akadem. Titel-FK aus GET /contact-title
  titleLabel?: string;           // Person: Anzeige des Titels (z. B. „Dr.")
  birthDate?: string;            // Person, optional (YYYY-MM-DD)
  // Anschrift (optional bei Person außerhalb Haushalt oder Firma)
  street?: string;
  postalCode?: string;
  city?: string;
  cityArea?: string;
  country?: string;
  ortsteil?: string;
  // Firma: einzelne Kanäle.
  phone?: string;                // Firma: Telefon (Person: Legacy, siehe *Private/*Work)
  mobile?: string;               // Firma: Mobil
  email?: string;                // Firma: E-Mail (Person: Legacy)
  web?: string;                  // Firma: Internet
  fax?: string;                  // Firma: Fax (z. B. von der Visitenkarte)
  // Person: getrennt nach privat/geschäftlich.
  phonePrivate?: string;
  phoneWork?: string;
  mobilePrivate?: string;
  mobileWork?: string;
  emailPrivate?: string;
  emailWork?: string;
  branche?: string;              // Firma: Anzeigepfad der HAUPTbranche (→ Contact.industry)
  branchengruppeId?: number;     // Firma: Branchengruppe der Hauptbranche (Zwei-Ebenen-Katalog)
  brancheFreitext?: string;      // Firma: „Sonstiges"-Freitext der Hauptbranche
  /**
   * Firma: weitere Branchengruppen neben der Hauptbranche. Sie brauchen die
   * Kontakt-id und werden nach dem Anlegen über `/contact/:id/branchengruppen`
   * nachgereicht — genau wie in der Vollanlage.
   *
   * MIT Freitext (seit 09.09.2026): Vorher standen hier nur die ids. Seit die
   * Auffangbranche ihre Auffanggruppe selbst anhakt, erscheint das Freitextfeld
   * auch an einer ZWEITbranche von allein — und schrieb bis dahin ins Leere:
   * Das Popup warf den Text bei jedem Tastendruck weg, das Feld war nach jedem
   * Zeichen wieder leer und damit unbenutzbar.
   *
   * Die reine Zahl bleibt gültig: Ein Anlage-Fenster, das VOR dieser Änderung
   * geöffnet und als JSON zwischengespeichert wurde, trägt sie noch. Gelesen
   * wird deshalb ausschließlich über `weitereBranchenNormalisieren`.
   */
  weitereBranchengruppen?: (number | WeitereBranchengruppe)[];
  /**
   * Firma: Schlüssel eines bestätigten Logo-Vorschlags („Ist das das Logo der
   * Firma?" → Ja) aus `utils/logoVorschlag`. Das Bild selbst steht NICHT
   * hier (der Verweis wird als JSON zwischengespeichert), übernommen wird es
   * nach dem Anlegen der Firma.
   */
  logoVorschlag?: string;
  legalForm?: string;            // Firma: Rechtsform (Anzeige-Label)
  legalFormId?: number;          // Firma: Rechtsform-FK (ContactLegalForm)
  /**
   * Firma: konkrete Bezeichnung, wenn der Katalogeintrag selbst keine benennt
   * („Sonstige", „Ausländische Rechtsform") → `Contact.rechtsform`.
   */
  rechtsform?: string;
  weitereKommunikation?: WeitereKommunikationEintrag[];
  // Firma: Steuer- und Registerangaben, z. B. von einer Visitenkarte (P4).
  umsatzsteuernummer?: string;
  register?: string;             // „HRB", „HRA" …
  handelsregisterNummer?: string;
  registergericht?: string;
  displayName?: string;          // berechneter Anzeigename
  /** Person ist verstorben (Contact.deceased) — Kreuz im Organigramm. */
  deceased?: boolean;
  /**
   * Nur ANZEIGE in den Beziehungslisten (User 28.09.2026), nie gespeichert:
   * Primär-Status des bestehenden Kontakts …
   */
  status?: string;
  /**
   * … und seine Telefonnummer/E-Mail (aus relations-deep). Fehlt das Objekt
   * oder der Wert (`undefined`), ist nichts bekannt; `null` heißt „hat keine".
   *
   * In der Akte einer Firma (`firmaId` gesetzt) sind es NUR die geschäftlichen
   * Wege mit Firmenbezug zu dieser Firma, samt ihrer ids — die Liste „Mensch im
   * Unternehmen" ändert sie direkt (contactToRelationItems.kommunikationVon).
   */
  kommunikation?: {
    telefon?: string | null;
    email?: string | null;
    telefonId?: number | null;
    emailId?: number | null;
    firmaId?: number;
  };
};

type Base = {
  /** Lokale UI-id (uid()), KEINE Backend-id. */
  id: string;
  ref: ContactRef | null;
  /**
   * Herkunft, wenn der Eintrag aus einer BESTEHENDEN Beziehung stammt — nur im
   * Umwandlungs-Modus gesetzt (siehe utils/contactToFormState.ts).
   *
   * Ohne diese Angaben kennt der Speicher-Pfad nur „neu anlegen" und erzeugte
   * beim Umwandeln Duplikate. `srcId`/`destId` gehören dazu, weil die
   * Backend-Deduplizierung (contactRelation.service.ts) richtungs-ABHÄNGIG
   * matcht: ein Richtungswechsel ergäbe sonst eine zweite Zeile neben der alten.
   */
  relationId?: number;
  /**
   * ALLE Backend-Zeilen, für die dieser Eintrag steht.
   *
   * Ein Kärtchen kann mehrere Rollen bündeln: „Geschäftsführer UND
   * Gesellschafter derselben Firma" sind zwei ContactRelation-Zeilen, in der
   * Maske aber EIN Eintrag (genau die Zusammenfassung, die die Detailansicht
   * seit #1062 zeigt). Ohne die vollständige Liste kennt der Abgleich nur die
   * erste Zeile — die zweite fiele in den Lösch-Schritt.
   *
   * `relationId` bleibt die erste davon (Einzelfall + Abwärtskompatibilität).
   */
  relationIds?: number[];
  srcId?: number;
  destId?: number;
};

/** (1) Firma: Geschäftsführer & Gesellschafter (Person ODER Firma als Gesellschafter). */
export type GfgItem = Base & {
  role: string;                  // GFG_ROLES
  share?: string;                // Anteile (nur Gesellschafter)
};

/** (2) Firma: Verantwortlichkeit/Zuständigkeit. */
export type VerantItem = Base & {
  funktion: string;              // VERANT_FUNCTIONS value
  isExtern: boolean;
};

/** (3) Firma: Verbindung zu anderer Firma (Beteiligung/Tochter/Mutter). */
/**
 * Eine weitere gespeicherte Zeile DERSELBEN Beteiligung — etwa die
 * Gesellschafter-Zeile neben der Tochterfirma (data/anteilsbeziehung.ts). Sie
 * steht nicht als eigener Eintrag in der Liste; der Abgleich behält sie, solange
 * der Eintrag dieselbe Beteiligung meint, und zieht die Quote mit.
 */
export type AnteilsSpiegel = { id: number; typ: string; srcId: number; destId: number };

export type FirmaConnItem = Base & {
  role: string;                  // FIRMA_CONN_ROLES
  share?: string;
  spiegel?: AnteilsSpiegel[];
  /** Leit- und Spiegelzeilen tragen verschiedene Quoten — nur Anzeige. */
  anteilWiderspruch?: string[];
};

/**
 * Eintritt/Austritt einer Verbindung (User 28.09.2026): `beginn` =
 * Eintrittsdatum (immer), `ende` = Austrittsdatum (nur bei `ausgeschieden`),
 * je YYYY-MM-DD. Gespeichert an der Beziehung
 * (ContactRelation.ausgeschieden/beginn/ende).
 */
export type AusgeschiedenFelder = {
  ausgeschieden?: boolean;
  beginn?: string;
  ende?: string;
};

/** Person (2): Verbindung zu Firma — Firma (ref) + volle Verantwortlichkeit-Eingabe. */
export type PersonFirmaItem = Base & AusgeschiedenFelder & {
  verant: VerantwortlichkeitValue | null; // intern/extern + GF/Gesellschafter/Katalog
  /**
   * Der Eintrag entstand, weil diese Firma als Arbeitgeber gewählt wurde
   * (utils/arbeitgeberVerbindung.ts). Er wird beim Lösen des Arbeitgebers
   * wieder entfernt, solange niemand ihn verändert hat. Gespeichert wird die
   * Verbindung über `PUT contact/:id/arbeitgeber`, nicht über diesen Eintrag.
   */
  autoAusArbeitgeber?: boolean;
};

/** Person (1): Beziehung zu Person / Haushalt (konkretes Label aus RELATIONSHIP_CATALOG). */
export type HouseholdItem = Base & {
  relationLabel: string;         // Katalog-Label oder RELATIONSHIP_FREE
  sameHousehold: boolean;        // true = Haushalt-Sektion, false = weitere Beziehungen
  freeRole?: string;             // Freitext Vorwärts („was other für self ist")
  freeRoleReverse?: string;      // Freitext Rückwärts („was self für other ist")
};

/** Statusrolle plus das Gegenüber, für das sie gilt. */
export type StatusBezugItem = Base & {
  /** Die Rolle aus `data/statusAuswahlKatalog.ts`. */
  rolle: string;
  /**
   * Gesetzt = die Rolle ist eine BEZIEHUNG zu einer Person und wird wie eine
   * Haushaltszeile gespeichert, nicht als erbrachte Funktion. Der Wert sagt
   * zugleich, ob die Person im selben Haushalt lebt.
   *
   * Warum nicht über den Katalog nachschlagen: Dann müsste `persistRelations`
   * wissen, aus welcher der beiden Listen die Rolle stammt. Die Maske weiß das
   * ohnehin und schreibt es ins Item — eine Quelle weniger, die auseinanderläuft.
   */
  imHaushalt?: boolean;
  /** Der Beziehungstyp selbst: aus RELATIONSHIP_CATALOG oder RELATIONSHIP_FREE. */
  verwandtschaft?: string;
  /** Freitext vorwärts („was die andere Person für diesen Kontakt ist"). */
  freitext?: string;
  /** Freitext rückwärts, optional. */
  freitextRueck?: string;
};

/** Eine Vertretungsrolle plus der Kontakt, für den sie ausgeübt wird. */
export type VertretungItem = Base & {
  /** Der Speicherwert aus VERTRETUNGSROLLEN, z. B. „Betreuer". */
  rolle?: string;
  /** Eine oder mehrere Vertretungsrollen (Multiselect). */
  rollen?: string[];
  fileUrl?: string;
  fileName?: string;
};

/** Persönliche Assistenz einer Leitungs- oder Privatperson. */
export type AssistantItem = Base & {
  contactId?: number;
  anrede?: string;
  title?: string;
  lastName?: string;
  firstName?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  extraCommunications?: WeitereKommunikationEintrag[];
  /** Wofür die Assistenz zuständig ist (Freitext) — landet in `function` der Beziehung. */
  aufgabe?: string;
};

/** Gesammelte Einträge aller Kategorien (an persistCollectedRelations übergeben). */
export type RelationCollections = {
  gfg?: GfgItem[];
  verant?: VerantItem[];
  firmaConn?: FirmaConnItem[];
  personFirma?: PersonFirmaItem[];
  household?: HouseholdItem[];   // Haushalt (sameHousehold=true)
  weitere?: HouseholdItem[];     // weitere Beziehungen zu Personen (sameHousehold=false)
  assistenz?: AssistantItem[];   // Persönliche Assistenz (relationType = "Private Assistenz")
  statusBezug?: StatusBezugItem[]; // „Verwalter — für wen?"
  vertretung?: VertretungItem[];   // „Dieser Kontakt vertritt …"
};

// ── Rollen-/Options-Listen (1:1 aus RelationCreateDialog übernommen) ──────────
export const GFG_ROLES = ["Geschäftsführer", "Gesellschafter", "Geschäftsführer und Gesellschafter"];
export const PERSON_FIRMA_ROLES = ["Geschäftsführer", "Gesellschafter", "Geschäftsführer und Gesellschafter", "Verantwortlichkeit"];
export const HOUSEHOLD_ARTEN = ["Ehepartner", "Lebenspartner", "Kind", "Sonstige"];

// Firma↔Firma steht seit 09/2026 in data/firmenverbindungen.ts — hier nur noch
// weitergereicht, damit die bestehenden Importpfade gültig bleiben.
export {
  FIRMA_CONN_ROLES,
  FIRMA_CONN_ROLE_VALUES,
  canonicalFirmaConn,
} from "../../data/firmenverbindungen";
export type { FirmaConnRolle } from "../../data/firmenverbindungen";

// Stabile lokale UI-id (keine Backend-id).
let _n = 0;
export const uid = (): string => `r${Date.now().toString(36)}${(_n++).toString(36)}`;

/** Anzeigename eines ContactRef für Card-Überschriften/Chips. */
export function refDisplayName(ref: ContactRef | null): string {
  if (!ref) return "—";
  if (ref.displayName) return ref.displayName;
  if (ref.type === "Firma") return (ref.lastName || "").trim() || "Neue Firma";
  const ln = (ref.lastName || "").trim();
  const fn = (ref.firstName || "").trim();
  return `${ln}${fn ? ", " + fn : ""}`.trim() || "Neue Person";
}

/** Hat der Ref genug, um (falls manuell) einen Kontakt anzulegen? */
export function refHasName(ref: ContactRef | null): boolean {
  if (!ref) return false;
  if (ref.contactId) return true;
  return !!(ref.lastName && ref.lastName.trim());
}

/** Wandelt ein AssistantItem in einen ContactRef um. */
export function assistantToRef(item: AssistantItem): ContactRef | null {
  if (item.contactId) {
    return {
      contactId: item.contactId,
      type: "Person",
      displayName: `${item.lastName ?? ""}${item.firstName ? ", " + item.firstName : ""}`.trim() || undefined,
      salutationLabel: item.anrede,
      titleLabel: item.title,
      lastName: item.lastName,
      firstName: item.firstName,
      phoneWork: item.phone,
      mobileWork: item.mobile,
      emailWork: item.email,
      weitereKommunikation: item.extraCommunications,
    };
  }
  if (!item.lastName?.trim()) return null;
  return {
    type: "Person",
    manual: true,
    salutationLabel: item.anrede,
    titleLabel: item.title,
    lastName: item.lastName.trim(),
    firstName: item.firstName?.trim() || undefined,
    phoneWork: item.phone?.trim() || undefined,
    mobileWork: item.mobile?.trim() || undefined,
    emailWork: item.email?.trim() || undefined,
    weitereKommunikation: item.extraCommunications,
  };
}

